import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { ApiService } from '../../abstract/api.service';
import { MemberOnboardingProgress } from '../response-models/member-onboarding-progress.model';
import { toMemberOnboardingProgress } from '../utils/onboarding-utils';

/**
 * Member (personal) onboarding progress: `/organizations/:id/onboarding/{progress, profile-completed,
 * safety-reviewed}` (hub `user-member-onboarding-controller.ts`). `OnboardingService` reads progress through
 * this service for the onboarding card; the wizard uses it to record steps.
 *
 * Failures reject with an `ApiError` (whose `message` is the hub's error).
 */
@Injectable({
  providedIn: 'root'
})
export class MemberOnboardingProgressService extends ApiService {
  constructor() {
    super('onboarding');
  }

  /** The signed-in member's progress in an organization. Rejects when the hub sends none. */
  async getProgress(organizationId: string): Promise<MemberOnboardingProgress> {
    const opts = { prefix: ['organizations', organizationId], path: 'progress' };
    const progress = await firstValueFrom(this.request('GET', opts, toMemberOnboardingProgress));
    if (!progress) {
      throw new ApiError(0, [], { code: 'EMPTY_RESPONSE' }, 'Failed to get onboarding progress');
    }
    return progress;
  }

  /** Mark the member's profile as completed. */
  async markProfileCompleted(organizationId: string): Promise<void> {
    await firstValueFrom(this.request('POST', { prefix: ['organizations', organizationId], path: 'profile-completed' }, () => undefined, {}));
  }

  /** Mark the safety guidelines as reviewed. */
  async markSafetyReviewed(organizationId: string): Promise<void> {
    await firstValueFrom(this.request('POST', { prefix: ['organizations', organizationId], path: 'safety-reviewed' }, () => undefined, {}));
  }

  /**
   * Calculate if onboarding is complete
   * Required: profile, Part 107, State Applicator License, safety guidelines
   *
   * @param progress Onboarding progress object
   * @returns True if all required items completed
   */
  isOnboardingComplete(progress: MemberOnboardingProgress): boolean {
    return (
      progress.profileCompleted &&
      progress.part107Uploaded &&
      progress.stateLicenseUploaded &&
      progress.safetyGuidelinesReviewed
    );
  }

  /**
   * Get remaining required items
   *
   * @param progress Onboarding progress object
   * @returns Array of incomplete required items
   */
  getRemainingRequiredItems(progress: MemberOnboardingProgress): string[] {
    const remaining: string[] = [];

    if (!progress.profileCompleted) {
      remaining.push('Complete Profile');
    }
    if (!progress.part107Uploaded) {
      remaining.push('Upload Part 107 Certificate');
    }
    if (!progress.stateLicenseUploaded) {
      remaining.push('Upload State Applicator License');
    }
    if (!progress.safetyGuidelinesReviewed) {
      remaining.push('Review Safety Guidelines');
    }

    return remaining;
  }

  /**
   * Get remaining optional items
   *
   * @param progress Onboarding progress object
   * @returns Array of incomplete optional items
   */
  getRemainingOptionalItems(progress: MemberOnboardingProgress): string[] {
    const remaining: string[] = [];

    if (!progress.medicalUploaded) {
      remaining.push('Upload Medical Certificate');
    }
    if (!progress.insuranceUploaded) {
      remaining.push('Upload Insurance Certificate');
    }

    return remaining;
  }
}
